import { AppBar, Toolbar, Typography, Box, IconButton, Avatar, Stack, useTheme, useMediaQuery, Menu as MuiMenu, MenuItem, Divider, ListItemIcon, Badge, Popover } from '@mui/material';
import { Menu, MenuOpen, Settings, Logout, Notifications, CalendarMonth } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth.js';
import { useUserPreferences } from '../hooks/useUserPreferences.js';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import socket from '../services/socket';
import AgendaComponent from './AgendaComponent';
import api from '../services/api';

export default function Header({ menuOpen, onToggleMenu }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const { userPhoto } = useUserPreferences();
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [todayEventCount, setTodayEventCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchTodayEvents = async () => {
    try {
      // Fetch for current month to filter today's events client-side,
      // to reuse existing endpoint logic without new backend route.
      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();

      const res = await api.get(`/agenda?mes=${month}&ano=${year}`);
      if (res.data.status === 'sucesso') {
        const todayStr = today.toISOString().split('T')[0];
        const count = res.data.dados.filter(e => e.dt_evento === todayStr).length;
        setTodayEventCount(count);
      }
    } catch (error) {
      console.error("Erro ao buscar contagem de eventos de hoje:", error);
    }
  };

  useEffect(() => {
    if (user?.id || user?.id_usuario) {
      fetchTodayEvents(); // Fetch agenda on mount

      const userId = user.id || user.id_usuario;
      socket.emit('join', userId);

      const handleReceiveMessage = (msg) => {
        if (location.pathname !== '/chat' && location.pathname !== '/Chat') {
          setUnreadCount(prev => prev + 1);
        }
      };

      socket.on('receive_message', handleReceiveMessage);

      return () => {
        socket.off('receive_message', handleReceiveMessage);
      };
    }
  }, [user, location.pathname]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCalendarOpen = (event) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const handlePreferences = () => {
    navigate('/preferencias');
    handleClose();
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleNotificationClick = () => {
    setUnreadCount(0);
    navigate('/chat');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer - 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
        color: theme.palette.mode === 'dark' ? '#fff' : '#000',
        boxShadow: theme.palette.mode === 'dark'
          ? '0px 2px 8px rgba(0, 0, 0, 0.3)'
          : '0px 2px 8px rgba(0, 0, 0, 0.1)',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        {/* Botão Menu Sanduíche */}
        <IconButton
          edge="start"
          color="inherit"
          onClick={onToggleMenu}
          aria-label="toggle menu"
          sx={{
            mr: 2,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          {menuOpen ? <MenuOpen sx={{ fontSize: '2rem' }} /> : <Menu sx={{ fontSize: '2rem' }} />}
        </IconButton>

        {/* Logo/Nome do Sistema */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.125rem', sm: '1.375rem' },
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            }}
          >
            GestCard
          </Typography>


        </Box>

        {/* Informações do Usuário */}
        {user && (
          <Stack direction="row" spacing={1} alignItems="center">

            <IconButton color="inherit" onClick={handleCalendarOpen}>
              <Badge badgeContent={todayEventCount} color="error">
                <CalendarMonth />
              </Badge>
            </IconButton>
            <Popover
              open={Boolean(calendarAnchorEl)}
              anchorEl={calendarAnchorEl}
              onClose={handleCalendarClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <AgendaComponent onEventsChange={fetchTodayEvents} />
            </Popover>

            <IconButton color="inherit" onClick={handleNotificationClick}>
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            {!isMobile && (
              <Box sx={{ textAlign: 'right', mr: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                  }}
                >
                  {user.st_nome || user.nome_completo || 'Usuário'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    lineHeight: 1.2,
                  }}
                >
                  {user.st_email || user.email || 'N/A'}
                </Typography>
              </Box>
            )}
            <IconButton onClick={handleMenu} sx={{ p: 0 }}>
              <Avatar
                src={userPhoto || undefined}
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  bgcolor: userPhoto ? 'transparent' : '#20ac9c',
                  border: userPhoto ? '2px solid #20ac9c' : 'none',
                  cursor: 'pointer',
                }}
              >
                {!userPhoto && (user.st_nome?.[0]?.toUpperCase() || user.nome_completo?.[0]?.toUpperCase() || 'U')}
              </Avatar>
            </IconButton>
            <MuiMenu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  minWidth: 180,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
            >
              <MenuItem onClick={handlePreferences}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                Preferências
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" sx={{ color: theme.palette.error.main }} />
                </ListItemIcon>
                <Typography color="error">Sair</Typography>
              </MenuItem>
            </MuiMenu>
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  );
}

